import type { ReactNode } from 'react'
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { useLocation } from 'react-router-dom'
import { mobileSidebarMediaQuery } from '@/lib/viewport'

const MOBILE_SIDEBAR_OPEN_CLASS = 'mobileSidebarOpen'

type MobileSidebarContextValue = {
  isOpen: boolean
  isMobileViewport: boolean
  open: () => void
  close: () => void
  toggle: () => void
}

const MobileSidebarContext = createContext<MobileSidebarContextValue | null>(null)

function useIsMobileViewport(): boolean {
  const [isMobile, setIsMobile] = useState(() => {
    if (typeof window === 'undefined') return false
    return window.matchMedia(mobileSidebarMediaQuery).matches
  })

  useEffect(() => {
    const media = window.matchMedia(mobileSidebarMediaQuery)
    const sync = () => setIsMobile(media.matches)
    sync()
    media.addEventListener('change', sync)
    return () => media.removeEventListener('change', sync)
  }, [])

  return isMobile
}

function MobileSidebarRouteSync({
  isOpen,
  close,
}: {
  isOpen: boolean
  close: () => void
}) {
  const location = useLocation()
  const pathnameRef = useRef(location.pathname)

  useEffect(() => {
    if (pathnameRef.current === location.pathname) return
    pathnameRef.current = location.pathname
    if (isOpen) close()
  }, [location.pathname, isOpen, close])

  return null
}

export function MobileSidebarProvider({ children }: { children: ReactNode }) {
  const [isOpen, setIsOpen] = useState(false)
  const isMobileViewport = useIsMobileViewport()

  const open = useCallback(() => setIsOpen(true), [])
  const close = useCallback(() => setIsOpen(false), [])
  const toggle = useCallback(() => setIsOpen((prev) => !prev), [])

  useEffect(() => {
    if (!isMobileViewport && isOpen) {
      setIsOpen(false)
    }
  }, [isMobileViewport, isOpen])

  useEffect(() => {
    const root = document.documentElement
    if (isOpen && isMobileViewport) {
      root.classList.add(MOBILE_SIDEBAR_OPEN_CLASS)
    } else {
      root.classList.remove(MOBILE_SIDEBAR_OPEN_CLASS)
    }
    return () => root.classList.remove(MOBILE_SIDEBAR_OPEN_CLASS)
  }, [isOpen, isMobileViewport])

  useEffect(() => {
    if (!isOpen || !isMobileViewport) return

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') close()
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [isOpen, isMobileViewport, close])

  const value = useMemo(
    () => ({
      isOpen,
      isMobileViewport,
      open,
      close,
      toggle,
    }),
    [isOpen, isMobileViewport, open, close, toggle]
  )

  return (
    <MobileSidebarContext.Provider value={value}>
      <MobileSidebarRouteSync isOpen={isOpen} close={close} />
      {children}
    </MobileSidebarContext.Provider>
  )
}

export function useMobileSidebar(): MobileSidebarContextValue {
  const context = useContext(MobileSidebarContext)
  if (!context) {
    throw new Error('useMobileSidebar must be used within MobileSidebarProvider')
  }
  return context
}
